/**
 * FeatureGate Component
 * Wraps features and shows upgrade prompt if user doesn't have access
 * 
 * Usage:
 *   <FeatureGate feature="voice_input">
 *     <VoiceInputComponent />
 *   </FeatureGate>
 * 
 * Or with custom fallback:
 *   <FeatureGate feature="bulk_upload" fallback={<CustomUpgradeUI />}>
 *     <BulkUploadComponent />
 *   </FeatureGate>
 */

import React from 'react';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { FEATURE_NAMES, TIER_INFO } from '@/config/tierFeatures';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Sparkles, Crown, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Default upgrade prompt component
const DefaultUpgradePrompt = ({ feature, requiredTier, requiredTierInfo, isTrialActive }) => {
  const navigate = useNavigate();
  const featureName = FEATURE_NAMES[feature] || feature;
  
  const tierColors = {
    premium: 'blue',
    deluxe: 'purple',
    enterprise: 'green'
  };
  
  const color = tierColors[requiredTier] || 'blue';

  return (
    <Card className={`border-2 border-${color}-200 bg-gradient-to-br from-${color}-50 to-white`}>
      <CardContent className="pt-6">
        <div className="text-center space-y-4">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-${color}-100`}>
            <Lock className={`w-8 h-8 text-${color}-600`} />
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              Upgrade to {requiredTierInfo?.name || 'Premium'}
            </h3>
            <p className="text-gray-600 mt-2">
              <span className="font-semibold">{featureName}</span> is available on the{' '}
              <span className={`font-bold text-${color}-600`}>
                {requiredTierInfo?.name || 'Premium'}
              </span>{' '}
              plan and above.
            </p>
          </div>

          <div className={`bg-${color}-50 rounded-xl p-4 border border-${color}-200`}>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Crown className={`w-5 h-5 text-${color}-600`} />
              <span className={`font-semibold text-${color}-800`}>
                {requiredTierInfo?.name} Plan
              </span>
            </div>
            <p className={`text-2xl font-bold text-${color}-700`}>
              {requiredTierInfo?.price}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Up to {requiredTierInfo?.maxChildren} children
            </p>
          </div>

          <Button
            onClick={() => navigate('/pricing')}
            className={`bg-${color}-600 hover:bg-${color}-700 text-white px-6 py-3`}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            View Upgrade Options
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          {isTrialActive && (
            <p className="text-sm text-amber-600 font-medium">
              This feature is available during your trial period!
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Inline upgrade prompt (smaller, for buttons/small areas)
export const InlineUpgradePrompt = ({ feature, requiredTier }) => {
  const navigate = useNavigate();
  const tierInfo = TIER_INFO[requiredTier];
  
  return (
    <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
      <Lock className="w-4 h-4 text-amber-600" />
      <span className="text-sm text-amber-800">
        Requires {tierInfo?.name || 'Premium'} plan
      </span>
      <Button
        size="sm"
        variant="outline"
        className="ml-auto border-amber-300 text-amber-700 hover:bg-amber-100"
        onClick={() => navigate('/pricing')}
      >
        Upgrade
      </Button>
    </div>
  );
};

// Locked button variant
export const LockedButton = ({ feature, children, className, ...props }) => {
  const { hasAccess, requiredTier, requiredTierInfo } = useFeatureAccess(feature);
  const navigate = useNavigate();

  if (hasAccess) {
    return (
      <Button className={className} {...props}>
        {children}
      </Button>
    );
  }

  return (
    <Button
      className={`${className} opacity-75 cursor-not-allowed`}
      onClick={() => navigate('/pricing')}
      title={`Requires ${requiredTierInfo?.name || 'Premium'} plan`}
    >
      <Lock className="w-4 h-4 mr-2" />
      {children}
    </Button>
  );
};

// Locked overlay - shows content with a lock overlay if user doesn't have access
export const LockedOverlay = ({ feature, children, className = "" }) => {
  const { hasAccess, requiredTier, requiredTierInfo } = useFeatureAccess(feature);
  const navigate = useNavigate();

  if (hasAccess) {
    return <>{children}</>;
  }

  return (
    <div className={`relative ${className}`}>
      {/* Show the actual content but grayed out */}
      <div className="opacity-40 pointer-events-none select-none">
        {children}
      </div>
      
      {/* Lock overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-lg">
        <div className="text-center p-4 bg-white rounded-xl shadow-lg border-2 border-amber-200 max-w-xs">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mb-3">
            <Lock className="w-6 h-6 text-amber-600" />
          </div>
          <p className="font-semibold text-gray-800 mb-1">
            {requiredTierInfo?.name || 'Premium'} Feature
          </p>
          <p className="text-sm text-gray-600 mb-3">
            Upgrade to unlock this feature
          </p>
          <Button
            size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-white"
            onClick={() => navigate('/pricing')}
          >
            <Sparkles className="w-4 h-4 mr-1" />
            Upgrade Now
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main FeatureGate component
const FeatureGate = ({ 
  feature, 
  children, 
  fallback = null,
  showUpgradePrompt = true,
  inline = false 
}) => {
  const { hasAccess, isLoading, requiredTier, requiredTierInfo, isTrialActive } = useFeatureAccess(feature);

  // CRITICAL FIX: While loading, show children to prevent lockout
  // The hook now defaults to 'signature' (full access) while loading anyway
  if (isLoading) {
    return <>{children}</>;
  }

  // User has access - render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // User doesn't have access - show upgrade prompt or fallback
  if (fallback) {
    return <>{fallback}</>;
  }

  if (!showUpgradePrompt) {
    return null;
  }

  if (inline) {
    return <InlineUpgradePrompt feature={feature} requiredTier={requiredTier} />;
  }

  return (
    <DefaultUpgradePrompt
      feature={feature}
      requiredTier={requiredTier}
      requiredTierInfo={requiredTierInfo}
      isTrialActive={isTrialActive}
    />
  );
};

export default FeatureGate;
